import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'

import PerText from '../Text'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import PerButtonCircle from '../ButtonCircle'
import { PerTag } from '../Tag'
import { ObjectiveStatus } from '../../../types'
import { TargetModalType } from './types'
import { useTargetsModalStore } from './TargetsModalStore'
import { isDefined } from '../../../types.helper'

export const TargetsModalHeader: FC = () => {
  const { t } = useTranslation()

  const [closeModal, cleanModal, currentObjective, targetModalType, modalProps] = useTargetsModalStore(
    (state) => [state.closeTargetModal, state.cleanTargetModal, state.currentObjective, state.targetModalType, state.modalProps],
    shallow,
  )

  const handleClose = () => {
    closeModal()
    setTimeout(() => {
      cleanModal()
    }, 300)
  }

  const renderHeader = () => {
    if (
      targetModalType === TargetModalType.viewTarget ||
      targetModalType === TargetModalType.viewTargetInMeeting ||
      targetModalType === TargetModalType.resignedEmployeViewTarget ||
      targetModalType === TargetModalType.appraisalEditActiveTarget
    ) {
      return t('targetModal.viewTarget')
    } else if (targetModalType === TargetModalType.appraisalSuggestedTarget && !currentObjective) {
      return t('targetModal.targetSuggestion')
    } else if (targetModalType === TargetModalType.appraisalSuggestedTarget && !!currentObjective) {
      return t('targetModal.editTargetSuggestion')
    } else if (targetModalType === TargetModalType.appraisalNewTarget && !currentObjective) {
      return t('targetModal.newTarget')
    } else if (targetModalType === TargetModalType.appraisalNewTarget && !!currentObjective) {
      return t('targetModal.editNewTarget')
    } else if (targetModalType === TargetModalType.appraisalEditPastTarget && !modalProps.pastTargetsPaymentView) {
      return t('targetModal.editPastTarget')
    } else if (targetModalType === TargetModalType.appraisalEditPastTarget && modalProps.pastTargetsPaymentView) {
      return t('targetModal.editPaymentTarget')
    } else if (targetModalType === TargetModalType.appraisalNewPastTarget) {
      return t('targetModal.newPastTarget')
    } else if (
      [TargetModalType.duplicateAndDismissOrDeleteTarget, TargetModalType.duplicateAndDismissOrDeletePastTarget].includes(
        targetModalType,
      ) &&
      !!currentObjective &&
      isDefined(currentObjective.targetAmount) &&
      (currentObjective.id === null || currentObjective.id === undefined)
    ) {
      return t('targetModal.duplicateAndDelete')
    } else if (
      [TargetModalType.duplicateAndDismissOrDeleteTarget, TargetModalType.duplicateAndDismissOrDeletePastTarget].includes(
        targetModalType,
      ) &&
      !!currentObjective &&
      isDefined(currentObjective.targetAmount) &&
      currentObjective.id !== null &&
      currentObjective.id !== undefined
    ) {
      return t('targetModal.duplicateAndDismiss')
    } else if (targetModalType === TargetModalType.deleteTarget) {
      return t('targetModal.delete')
    } else {
      return null
    }
  }

  const renderTargetTag = () => {
    if (!currentObjective) {
      return null
    }

    if (currentObjective.status === ObjectiveStatus.Proposal || currentObjective.status === ObjectiveStatus.ProposalFromManager) {
      return (
        <PerTag color="cyan" className="noMargin">
          {t('okr.objective.status.proposal')}
        </PerTag>
      )
    } else if (
      currentObjective.status === ObjectiveStatus.MarkedAsDone ||
      currentObjective.status === ObjectiveStatus.PastApproved ||
      (currentObjective.status === ObjectiveStatus.Approved && !!currentObjective.achievedAmount)
    ) {
      return (
        <PerTag color="gold" className="noMargin">
          {t('okr.objective.status.markedAsDone')}
        </PerTag>
      )
    } else if (currentObjective.status === ObjectiveStatus.Approved) {
      return (
        <PerTag color="lime" className="noMargin">
          {t('okr.objective.status.approved')}
        </PerTag>
      )
    } else if (currentObjective.status === ObjectiveStatus.DismissedPending) {
      return (
        <PerTag color="red" className="noMargin">
          {t('okr.objective.status.dismissedPending')}
        </PerTag>
      )
    } else {
      return <PerTag className="noMargin">{t('okr.objective.status.closed')}</PerTag>
    }
  }

  return (
    <>
      <PerFlexRow justify="space-between" align="middle">
        <PerFlexRow>
          <PerText fontSize="xl" color="primary">
            {renderHeader()}
          </PerText>
        </PerFlexRow>
        <PerButtonCircle size="large" icon="close_stroke" color="transparent" onClick={handleClose} testid="tm-modal-close" />
      </PerFlexRow>
      {!!currentObjective && (
        <PerFlexRow align="middle">
          <PerText fontSize="m" color="secondary">
            {!!currentObjective.targetAmount ? t('targetModal.monetary') : t('targetModal.development')}
          </PerText>
          <PerSpacer ml="xs" />
          {renderTargetTag()}
        </PerFlexRow>
      )}
    </>
  )
}
